import {
  CButton,
  CCol,
  CContainer,
  CHeader,
  CHeaderBrand,
  CHeaderNav,
  CHeaderToggler,
  CImage,
  CInputGroup,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import React, { useEffect, useState } from 'react'
import Autocomplete from 'react-autocomplete'
import { useHistory, useLocation } from 'react-router-dom'
import { cilMenu } from '@coreui/icons'
import AppHeaderDropdown from './AppHeaderDropdown'
import { reduxServices } from '../reducers/reduxServices'
import { useAppDispatch, useTypedSelector } from '../stateStore'
import { logo } from '../assets/brand/logo'
import { baseImageExtension } from '../pages/Achievements/AchievementConstants'
import usePermissionAccess from '../middleware/hooks/usePermissionAccess'
import { EmploymentStatus } from '../types/EmployeeDirectory/EmployeesList/employeeListTypes'

type SearchedCache = {
  rootLocation: string
  searchKey: string
}

const AppHeader = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const permission = usePermissionAccess()

  const [searchCache, setSearchCache] = useState<SearchedCache[]>(
    [] as SearchedCache[],
  )
  const location = useLocation()
  const unfoldable = useTypedSelector((state) => state.app.sidebarUnfoldable)
  const employeePolicyAgreementHistory = useTypedSelector(
    reduxServices.EmployeeHandbook.selectors.employeePolicyAgreementHistory,
  )
  const history = useHistory()
  const employeeListPath = '/employeeList'
  const [searchAutoCompleteTarget, setSearchAutoCompleteTarget] =
    useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [isDisabled, setIsDisabled] = useState<boolean>(false) ////ah indota i redux ani oi

  const employees = useTypedSelector(
    reduxServices.searchEmployee.selectors.allEmployees,
  )

  const userAccessToSearch = permission.accessibility('Employee Directory')

  const employeeResult = employees.filter(
    (item) => item.fullName === searchValue,
  )

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem('searchLength', '0')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const saveToCache = (location: string, key: string) => {
    const indexOfLocation = searchCache?.findIndex(
      (loc) => loc.rootLocation === location,
    )
    if (searchCache.length === 0) {
      localStorage.setItem('searchLength', '1')
    }
    if (indexOfLocation >= 0) {
      const cache = searchCache
      cache[indexOfLocation] = {
        ...cache[indexOfLocation],
        searchKey: key,
      }
      setSearchCache(cache)
      localStorage.setItem('searchLength', String(searchCache.length))
      localStorage.setItem('isResetSearch', 'true')
      return
    }
    setSearchCache((prev) => [
      ...prev,
      { rootLocation: location, searchKey: key } as SearchedCache,
    ])
    localStorage.setItem('searchLength', searchCache.length.toString())
    localStorage.setItem('isResetSearch', 'true')
  }

  const referFromCache = (locationValue: string) => {
    const indexOfLocation = searchCache?.findIndex(
      (loc) => loc.rootLocation === locationValue,
    )
    if (indexOfLocation >= 0) return searchCache[indexOfLocation].searchKey
    return ''
  }

  const employeeProfileString = 'employeeProfile'
  const editEmployeeString = 'editEmployee'
  const employeeListString = 'employeeList'

  const allowedURLs =
    location.pathname.includes(employeeProfileString) ||
    location.pathname.includes(editEmployeeString) ||
    location.pathname.includes(employeeListString)
  useEffect(() => {
    const cachedValue = referFromCache(location.pathname)
    setSearchAutoCompleteTarget(cachedValue)
    if (!allowedURLs) {
      if (searchValue) dispatch(reduxServices.searchEmployee.searchEmployee(''))
      setSearchValue('')
    }
  }, [location.pathname])

  useEffect(() => {
    if (!userAccessToSearch?.viewaccess) return
    if (searchValue && isSearching) {
      if (!isNaN(Number(searchValue))) return
      localStorage.setItem('isResetSearch', 'false')
      setIsSearching(false)
      dispatch(reduxServices.searchEmployee.searchEmployee(searchValue))
    }
    if (!searchValue) {
      dispatch(reduxServices.searchEmployee.searchEmployee(''))
    }
  }, [searchValue, isSearching, userAccessToSearch?.viewaccess])
  const onHandleSelectEmployee = (fullName: string) => {
    localStorage.setItem('isResetSearch', 'true')
    setSearchAutoCompleteTarget(fullName)
    setSearchValue(fullName)
    saveToCache(location.pathname, fullName)
  }

  const selectedEmploymentStatus = useTypedSelector(
    reduxServices.employeeList.selectors.getSelectedStatusValue,
  )

  const handleArrowDown = () => {
    setHighlightedIndex((prevIndex) =>
      prevIndex < employees.length - 1 ? prevIndex + 1 : prevIndex,
    )
  }

  const handleArrowUp = () => {
    setHighlightedIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : prevIndex,
    )
  }

  const handleEnter = () => {
    if (highlightedIndex !== -1) {
      const value = employees[highlightedIndex]?.fullName
      setSearchValue(value || '')
      onHandleSelectEmployee(value || '')
      handleSearchEmployee(value || '')
      dispatch(reduxServices.app.actions.setSearchString(value || ''))
      setHighlightedIndex(-1)
    } else {
      handleSearchEmployee(searchValue)
      setIsSearching(false)
      saveToCache(location.pathname, searchValue)
      dispatch(reduxServices.app.actions.setSearchString(searchValue))
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        handleArrowDown()
        break
      case 'ArrowUp':
        handleArrowUp()
        break
      case 'Enter':
        handleEnter()
        break
      default:
        break
    }
  }

  const handleSearchEmployee = async (searchKey: string) => {
    if (location.pathname !== employeeListPath) {
      // set the filter option back to Active
      dispatch(
        reduxServices.employeeList.actions.setStatusValue(
          EmploymentStatus.active,
        ),
      )

      let temp = searchKey
      if (employeeResult[0] !== undefined) temp = employeeResult[0].fullName
      dispatch(
        reduxServices.employeeList.getEmployees({
          startIndex: 0,
          endIndex: 20,
          selectionStatus: selectedEmploymentStatus,
          searchStr: temp,
          selectionDate: '',
          from: '',
          to: '',
          country: '',
        }),
      )
      saveToCache(location.pathname, temp)
      dispatch(reduxServices.app.actions.setSearchString(temp))
      localStorage.setItem('isResetSearch', 'true')
      history.push(employeeListPath)
    } else {
      dispatch(
        reduxServices.employeeList.getEmployees({
          startIndex: 0,
          endIndex: 20,
          selectionStatus: selectedEmploymentStatus,
          searchStr: searchKey,
          selectionDate: '',
          from: '',
          to: '',
          country: '',
        }),
      )
      if (!searchKey && location.pathname === employeeListPath) {
        return
      }
      const searchEmployeeResultAction = await dispatch(
        reduxServices.searchEmployee.searchEmployee(searchKey),
      )
      if (
        reduxServices.searchEmployee.searchEmployee.fulfilled.match(
          searchEmployeeResultAction,
        )
      ) {
        history.push(employeeListPath)
      }
    }
  }

  const handleSearchButtonToggle = () => {
    setIsSearching(false)
    handleSearchEmployee(searchValue)
    saveToCache(location.pathname, searchValue)
    dispatch(reduxServices.app.actions.setSearchString(searchValue))
  }

  const highlightMatches = (text: string) => {
    const regex = new RegExp(`(${searchValue})`, 'gi')
    return text?.replace(regex, '<span class="selected-text">$1</span>')
  }
  const handleClick = () => {
    dispatch(reduxServices.app.actions.toggleSidebar())
  }

  useEffect(() => {
    if (!unfoldable) {
      window.localStorage.setItem('smin', '1')
    } else {
      localStorage.removeItem('smin')
    }
  }, [!unfoldable])

  const disabledStyle: React.CSSProperties = {
    pointerEvents: 'none',
    opacity: 0.5,
  }
  useEffect(() => {
    if (
      employeePolicyAgreementHistory[0]?.id === null &&
      employeePolicyAgreementHistory[0]?.country
    ) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [employeePolicyAgreementHistory])
  return (
    <CHeader className="main-header mb-3">
      <CContainer fluid className="xcontainer-fluid">
        <CHeaderBrand className="mx-auto d-none">
          <CIcon icon={logo} height={48} />
        </CHeaderBrand>
        <CHeaderToggler
          // eslint-disable-next-line
          className={`ps-1 me-auto ${
            (!unfoldable && !('smin' in localStorage)) ||
            unfoldable ||
            'smin' in localStorage
              ? ''
              : 'hide-me'
          }`}
          onClick={handleClick}
        >
          <CIcon icon={cilMenu} size="lg" />
        </CHeaderToggler>
        <CHeaderNav
          className="ps-1 ms-auto"
          style={isDisabled ? disabledStyle : {}}
        >
          {userAccessToSearch?.viewaccess && (
            <CInputGroup className="global-search me-4">
              <Autocomplete
                inputProps={{
                  className: 'form-control form-control-sm search-header',
                  id: 'employee-autocomplete',
                  placeholder: 'Search Employee',
                  onKeyDown: handleKeyDown,
                }}
                autoHighlight={false}
                getItemValue={(item) => item?.fullName}
                items={employees && employees?.slice(0, 10)}
                wrapperProps={{ className: 'search-wrapper' }}
                wrapperStyle={{ position: 'relative' }}
                renderMenu={(children) => (
                  <div
                    className={
                      searchAutoCompleteTarget &&
                      searchAutoCompleteTarget.length > 0
                        ? 'autocomplete-dropdown-wrap search-employee-list employee-search-header'
                        : 'autocomplete-dropdown-wrap hide-wrapper search-employee-list employee-search-header'
                    }
                  >
                    {children}
                  </div>
                )}
                renderItem={(item, isHighlighted) => {
                  const imageUrl = item?.profilePicPath
                  const baseUrl = baseImageExtension
                  const url = new URL(imageUrl, baseUrl)
                  const finalImageUrl = url.href

                  return (
                    <div
                      data-testid="employee-options"
                      className="autocomplete-dropdown-item"
                      style={{
                        backgroundColor: isHighlighted ? 'powderblue' : 'white',
                      }}
                      key={item.id}
                    >
                      <CCol className="d-flex justify-content-left employee-wrapper">
                        <CImage
                          className="birthday-avatar"
                          src={finalImageUrl}
                        />
                        <div className="p-1">
                          <p className="m-0 employee-fullname">
                            <span
                              dangerouslySetInnerHTML={{
                                __html: highlightMatches(item.fullName),
                              }}
                            />
                          </p>
                          <span className="employee-desg">
                            {item.designation}
                          </span>
                        </div>
                      </CCol>
                    </div>
                  )
                }}
                value={searchValue}
                shouldItemRender={(item, value) =>
                  item.fullName.toLowerCase().indexOf(value.toLowerCase()) > -1
                }
                // onMenuVisibilityChange={(isOpen) => setIsMenuOpen(isOpen)}
                onChange={(e) => {
                  // localStorage.setItem('isResetSearch', 'false')
                  setIsSearching(true)
                  setSearchValue(e.target.value)
                  setSearchAutoCompleteTarget(e.target.value)
                  dispatch(
                    reduxServices.app.actions.setSearchString(e.target.value),
                  )
                }}
                onSelect={(value) => {
                  setSearchValue(value)
                  setIsSearching(false)
                  onHandleSelectEmployee(value)
                  setIsSearching(false)
                  handleSearchEmployee(value)
                  dispatch(reduxServices.app.actions.setSearchString(value))
                }}
              />
              <CButton
                type="button"
                color="primary"
                id="button-addon2"
                data-testid="search-employee-btn"
                onClick={handleSearchButtonToggle}
              >
                <i className="fa fa-search"></i>
              </CButton>
            </CInputGroup>
          )}
        </CHeaderNav>
        <CHeaderNav>
          <AppHeaderDropdown />
        </CHeaderNav>
      </CContainer>
    </CHeader>
  )
}

export default AppHeader
